import { Box, Button, Divider, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClone, faFileLines, faReceipt, faTrashCan } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    menuPaper: {
        minWidth: "22rem",
        borderRadius: ".75rem",
    },
    divider: {
        marginTop: ".5rem",
        marginBottom: ".5rem",
    },
    kebabSection: {
        padding: ".5rem",
    },
    kebabSectionLabel: {
        color: theme.palette.tailwind.slate[400],
        marginBottom: ".5rem",
        paddingLeft: ".25rem",
    },
    docButtons: {
        display: "flex",
        gap: ".5rem",
    },
    docButton: {
        "&.MuiButton-root": {
            justifyContent: "flex-start",
            padding: ".75rem",
            borderRadius: ".75rem",
            border: `1px solid ${theme.palette.tailwind.slate[200]}`,
            color: theme.palette.tailwind.slate[800],
            fontWeight: 700,
            textTransform: "none",
            whiteSpace: "nowrap",
        },
    },
    kebabMenuItem: {
        "&.MuiMenuItem-root": {
            minHeight: "auto",
            paddingTop: ".75rem",
            paddingBottom: ".75rem",
        },
    },
    kebabItemIcon: {
        minWidth: "2rem",
        color: theme.palette.tailwind.slate[500],
    },
    deleteItem: {
        color: theme.palette.error.main,
    },
}))

type Props = Readonly<{
    anchorEl: HTMLElement | null
    onClose: () => void
    onPackingSlip: () => void
    onReceipt: () => void
    onDuplicate: () => void
    onDelete: () => void
}>

export function OrderKebabMenu({ anchorEl, onClose, onPackingSlip, onReceipt, onDuplicate, onDelete }: Props) {
    const { classes, cx } = useStyles()

    const runAndClose = (action: () => void) => () => {
        action()
        onClose()
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { className: classes.menuPaper } }}
        >
            <Box className={classes.kebabSection}>
                <LocalizedStrict id="meeting-service-order-drawer-documents">
                    <Typography
                        component="div"
                        variant="caption"
                        fontWeight={700}
                        letterSpacing=".05em"
                        className={classes.kebabSectionLabel}
                    >
                        DOCUMENTS
                    </Typography>
                </LocalizedStrict>
                <Box className={classes.docButtons}>
                    <LocalizedStrict id="meeting-service-table-production-slip-button">
                        <Button
                            variant="outlined"
                            className={classes.docButton}
                            startIcon={<FontAwesomeIcon icon={faFileLines} />}
                            onClick={runAndClose(onPackingSlip)}
                        >
                            Packing slip
                        </Button>
                    </LocalizedStrict>
                    <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                        <Button
                            variant="outlined"
                            className={classes.docButton}
                            startIcon={<FontAwesomeIcon icon={faReceipt} />}
                            onClick={runAndClose(onReceipt)}
                        >
                            Receipt
                        </Button>
                    </LocalizedStrict>
                </Box>
            </Box>
            <Divider className={classes.divider} />
            <MenuItem className={classes.kebabMenuItem} onClick={runAndClose(onDuplicate)}>
                <ListItemIcon className={classes.kebabItemIcon}>
                    <FontAwesomeIcon icon={faClone} />
                </ListItemIcon>
                <LocalizedStrict id="meeting-service-order-drawer-duplicate">
                    <span>Duplicate</span>
                </LocalizedStrict>
            </MenuItem>
            <Divider className={classes.divider} />
            <MenuItem className={cx(classes.kebabMenuItem, classes.deleteItem)} onClick={runAndClose(onDelete)}>
                <ListItemIcon className={cx(classes.kebabItemIcon, classes.deleteItem)}>
                    <FontAwesomeIcon icon={faTrashCan} />
                </ListItemIcon>
                <LocalizedStrict id="meeting-service-production-view-card-delete-button">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>
        </Menu>
    )
}
